import classNames from 'classnames';
import { ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { appRoutes } from 'config';
import { CtaButton } from './components/CtaButton';

interface Props {
  /** Overrides the homepage i18n content (e.g. for the About page). */
  title?: ReactNode;
  description?: ReactNode;
  startLabel?: ReactNode;
  pricingLabel?: ReactNode;
  startHref?: string;
  pricingHref?: string;
  reportLocation?: string;
}

export const Section17 = ({
  title,
  description,
  startLabel,
  pricingLabel,
  startHref = appRoutes.LOGIN,
  pricingHref,
  reportLocation = 'Homepage Final CTA',
}: Props = {}) => {
  const t = useTranslations();
  return (
    <section className={styles.section}>
      {/* bottom radial glow */}
      <div className={styles.glow} />

      <div className={styles.wrap}>
        <h2 className={styles.title}>
          {title ??
            t.rich('pages.home.section17.title', {
              highlight: (chunks) => (
                <span className={styles.emphasis}>{chunks}</span>
              ),
            })}
        </h2>
        <p className={styles.description}>
          {description ?? t('pages.home.section17.description')}
        </p>
        <div className={styles.cta}>
          <CtaButton
            variant="primary"
            href={startHref}
            reportLocation={reportLocation}
            withArrow
          >
            {startLabel ?? t('pages.home.section17.ctaStart')}
          </CtaButton>
          <CtaButton variant="outline" href={pricingHref}>
            {pricingLabel ?? t('pages.home.section17.ctaPricing')}
          </CtaButton>
        </div>
      </div>
    </section>
  );
};

const styles = {
  section:
    'relative overflow-hidden marketing-dark:bg-mf-navy pt-24 pb-[50px] text-center',
  glow: classNames(
    'pointer-events-none absolute left-1/2 bottom-[-200px] w-[1000px] h-[600px] max-w-none -translate-x-1/2',
    'bg-[radial-gradient(circle_at_42%_100%,rgba(189,222,255,0.55),transparent_65%),radial-gradient(circle_at_66%_100%,rgba(189,222,255,0.35),transparent_60%)]',
    'marketing-dark:bg-[radial-gradient(circle_at_50%_100%,rgba(40,80,200,0.4),transparent_65%)]',
  ),
  wrap: 'relative z-[2] max-w-[1200px] mx-auto px-6 lg:px-10',
  title:
    'mb-[18px] text-[34px] font-extrabold leading-[1.1] tracking-[-0.02em] text-mf-off-black-600 marketing-dark:text-white lg:text-[50px]',
  emphasis: 'text-mf-blue marketing-dark:text-mf-coral',
  description:
    'mx-auto mb-[30px] max-w-[620px] text-lg leading-[1.6] text-mf-gray-600 marketing-dark:text-white/72 lg:text-[19px]',
  cta: 'flex flex-wrap justify-center gap-[13px]',
};
